import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Calculator, BarChart2, Building2 } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './DepreciationCalculatorPage/components/CalculationForm';
import ResultsOverview from './DepreciationCalculatorPage/components/ResultsOverview';
import DepreciationSchedule from './DepreciationCalculatorPage/components/DepreciationSchedule';
import QuickAdjustmentSidebar from './DepreciationCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './DepreciationCalculatorPage/components/ComparisonSidebar';
import TaxMethodsSidebar from './DepreciationCalculatorPage/components/TaxMethodsSidebar';
import InfoContent from './DepreciationCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  assetName: 'Новое оборудование',
  assetCost: '1000000',
  salvageValue: '100000',
  usefulLife: '10', // лет
  depreciationMethod: 'straight_line', // 'straight_line', 'reducing_balance', 'sum_of_years', 'units_of_production'
  startDate: new Date().toISOString().split('T')[0],
  assetType: 'equipment',
  assetGroup: '3', // Группа по ОКОФ (Общероссийский классификатор основных фондов)
  taxMethod: 'linear', // 'linear', 'non_linear'
  specialCoefficient: '1', // Специальный коэффициент (1, 2 или 3)
  includeTaxCalculation: true,
  includeAccelerationCoefficient: false, 
  accelerationCoefficient: '2',
  productionUnits: '100000', // Для метода пропорционально объему продукции
  firstYearUnits: '10000', // Плановые единицы продукции для первого года
  includeMonthlySplit: false, // Разбивка по месяцам
  includeRevaluation: false, // Учет переоценки
  revaluationAmount: '0', 
  revaluationYear: '3',
  revaluationType: 'increase', // 'increase' или 'decrease'
};

// Определение типов основных средств
export const assetTypes = [
  { id: 'equipment', name: 'Оборудование', defaultGroup: '3' },
  { id: 'building', name: 'Здания и сооружения', defaultGroup: '10' },
  { id: 'vehicle', name: 'Транспортные средства', defaultGroup: '4' },
  { id: 'computer', name: 'Компьютеры и электроника', defaultGroup: '2' },
  { id: 'furniture', name: 'Мебель и офисное оборудование', defaultGroup: '4' },
  { id: 'land_improvement', name: 'Благоустройство земельных участков', defaultGroup: '5' },
  { id: 'intangible', name: 'Нематериальные активы', defaultGroup: '2' }
];

// Группы амортизации по ОКОФ с минимальными сроками
export const depreciationGroups = [
  { id: '1', name: 'Группа 1 (1-2 года)', minYears: 1, maxYears: 2 },
  { id: '2', name: 'Группа 2 (2-3 года)', minYears: 2, maxYears: 3 },
  { id: '3', name: 'Группа 3 (3-5 лет)', minYears: 3, maxYears: 5 },
  { id: '4', name: 'Группа 4 (5-7 лет)', minYears: 5, maxYears: 7 },
  { id: '5', name: 'Группа 5 (7-10 лет)', minYears: 7, maxYears: 10 },
  { id: '6', name: 'Группа 6 (10-15 лет)', minYears: 10, maxYears: 15 },
  { id: '7', name: 'Группа 7 (15-20 лет)', minYears: 15, maxYears: 20 },
  { id: '8', name: 'Группа 8 (20-25 лет)', minYears: 20, maxYears: 25 },
  { id: '9', name: 'Группа 9 (25-30 лет)', minYears: 25, maxYears: 30 },
  { id: '10', name: 'Группа 10 (свыше 30 лет)', minYears: 30, maxYears: 50 }
];

/**
 * DepreciationCalculatorPage Component - Полный калькулятор амортизации 
 * основных средств с различными методами расчета и дополнительными параметрами.
 */
const DepreciationCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('depreciation-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор амортизации основных средств | Gensio';
  const metaDescription = tool?.metaDescription || 'Расчет амортизации основных средств различными методами: линейный, уменьшаемого остатка, по сумме чисел лет и пропорционально объему продукции с учетом российских стандартов бухгалтерского и налогового учета.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedAssets, setSavedAssets] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedAssetsData = localStorage.getItem('savedDepreciationAssets');
    if (savedAssetsData) {
      try {
        setSavedAssets(JSON.parse(savedAssetsData));
      } catch (e) {
        console.error("Ошибка загрузки сохраненных расчетов:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'straight-line':
          newState.depreciationMethod = 'straight_line';
          break;
        case 'reducing-balance':
          newState.depreciationMethod = 'reducing_balance';
          break;
        case 'sum-of-years':
          newState.depreciationMethod = 'sum_of_years';
          break;
        case 'units-of-production':
          newState.depreciationMethod = 'units_of_production';
          break;
        case 'tax-linear':
          newState.taxMethod = 'linear';
          newState.includeTaxCalculation = true;
          break;
        case 'tax-non-linear':
          newState.taxMethod = 'non_linear';
          newState.includeTaxCalculation = true;
          break;
        case 'special-coefficients':
          newState.includeAccelerationCoefficient = true;
          newState.accelerationCoefficient = '2';
          break;
        case 'buildings':
          newState.assetType = 'building';
          newState.assetGroup = '10';
          break;
        case 'equipment':
          newState.assetType = 'equipment';
          newState.assetGroup = '3';
          break;
        case 'vehicles':
          newState.assetType = 'vehicle';
          newState.assetGroup = '4';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setSchedule([]);
    setErrors({});
    setShowFullSchedule(false);

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveAsset = (name) => {
    if (!results) return;

    const newSavedAsset = {
      id: Date.now().toString(),
      name: name || `${formData.assetName} (${formData.assetCost}₽)`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      method: formData.depreciationMethod
    };

    const updatedSavedAssets = [...savedAssets, newSavedAsset];
    setSavedAssets(updatedSavedAssets);
    localStorage.setItem('savedDepreciationAssets', JSON.stringify(updatedSavedAssets));

    return newSavedAsset;
  };

  // Удаление сохраненного расчета
  const deleteSavedAsset = (id) => {
    const updatedSavedAssets = savedAssets.filter(asset => asset.id !== id);
    setSavedAssets(updatedSavedAssets);
    localStorage.setItem('savedDepreciationAssets', JSON.stringify(updatedSavedAssets));
  };

  // Загрузка сохраненного расчета
  const loadSavedAsset = (id) => {
    const assetToLoad = savedAssets.find(asset => asset.id === id);
    if (assetToLoad) {
      setFormData(assetToLoad.formData);
      setResults(assetToLoad.results);
      setSchedule(assetToLoad.results.schedule || []);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Building2 className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор амортизации основных средств'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              schedule={schedule}
              setSchedule={setSchedule}
              assetTypes={assetTypes}
              depreciationGroups={depreciationGroups}
              saveAsset={saveAsset}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} />
                <DepreciationSchedule 
                  schedule={schedule} 
                  results={results}
                  formData={formData}
                  showFullSchedule={showFullSchedule}
                  setShowFullSchedule={setShowFullSchedule}
                />
              </>
            )}

            {/* --- Информационный контент об амортизации --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              depreciationGroups={depreciationGroups}
            />

            {/* --- Панель сравнения расчетов --- */}
            <ComparisonSidebar 
              savedAssets={savedAssets}
              deleteSavedAsset={deleteSavedAsset}
              loadSavedAsset={loadSavedAsset}
            />

            {/* --- Панель методов налоговой амортизации --- */}
            <TaxMethodsSidebar 
              formData={formData}
              setFormData={setFormData}
            />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Depreciation Schedule Table */
        .depreciation-schedule-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .depreciation-schedule-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .depreciation-schedule-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .depreciation-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .depreciation-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .depreciation-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .depreciation-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .depreciation-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .depreciation-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default DepreciationCalculatorPage;